import type * as THREE from "three";
import { onScopeDispose, watch, type ComputedRef } from "vue";

import { getLayerRenderOrder } from "./useGridOverlays.ts";

import type {
  TStreamlineVectorField,
  TVectorVariablePair,
} from "@/lib/data/vectorField.ts";
import { StreamlineParticleLayer } from "@/lib/layers/streamlineParticles.ts";
import { ProjectionHelper } from "@/lib/projection/projectionUtils.ts";
import {
  BUILTIN_LAYER_IDS,
  LAYER_OPACITY,
  STREAMLINE_LOADING_STAGES,
  useGlobeControlStore,
} from "@/store/store.ts";

type TOptions = {
  getScene: () => THREE.Scene | undefined;
  redraw: () => void;
  projectionHelper: ComputedRef<ProjectionHelper>;
  onProjectionChange: (callback: () => void) => void;
  registerAnimationCallback: (
    callback: (deltaSeconds: number) => void
  ) => () => void;
};

type TStore = ReturnType<typeof useGlobeControlStore>;

function findLayerEntry(store: TStore) {
  return store.layerStack.find(
    (entry) => entry.id === BUILTIN_LAYER_IDS.STREAMLINES
  );
}

function syncAnimation(
  options: TOptions,
  layer: StreamlineParticleLayer,
  visible: boolean,
  stopAnimation?: () => void
) {
  if (visible && !stopAnimation) {
    return options.registerAnimationCallback((deltaSeconds) => {
      layer.update(deltaSeconds);
    });
  }
  if (!visible && stopAnimation) {
    stopAnimation();
    return undefined;
  }
  return stopAnimation;
}

// eslint-disable-next-line max-lines-per-function
export function useStreamlineLayer(options: TOptions) {
  const store = useGlobeControlStore();
  let layer: StreamlineParticleLayer | undefined;
  let stopAnimation: (() => void) | undefined;
  let disposed = false;
  let buildRevision = 0;

  function updateAppearance() {
    if (!layer) {
      return;
    }
    const entry = findLayerEntry(store);
    layer.setRenderOrder(
      getLayerRenderOrder(store.layerStack, BUILTIN_LAYER_IDS.STREAMLINES)
    );
    layer.setOpacity(entry?.opacity ?? LAYER_OPACITY.MAX);
    const visible = Boolean(entry?.visible && store.streamlineAvailable);
    layer.object.visible = visible;
    stopAnimation = syncAnimation(options, layer, visible, stopAnimation);
    options.redraw();
  }

  function removeLayerObject() {
    stopAnimation?.();
    stopAnimation = undefined;
    if (layer) {
      options.getScene()?.remove(layer.object);
      layer.dispose();
      layer = undefined;
    }
  }

  function disposeObject() {
    buildRevision++;
    store.streamlineLoading = false;
    store.streamlineProgress = undefined;
    removeLayerObject();
  }

  function installLayer(nextLayer: StreamlineParticleLayer) {
    nextLayer.updateProjection(options.projectionHelper.value);
    removeLayerObject();
    layer = nextLayer;
    options.getScene()?.add(layer.object);
    updateAppearance();
  }

  function clear(incompatibility?: string) {
    disposeObject();
    store.setStreamlinePair(undefined);
    store.streamlineIncompatibility = incompatibility;
  }

  function setAvailablePair(pair: TVectorVariablePair) {
    if (disposed) {
      return;
    }
    disposeObject();
    store.setStreamlinePair(pair);
  }

  function startLoading() {
    store.streamlineIncompatibility = undefined;
    store.streamlineLoading = true;
    store.streamlineProgress = undefined;
    store.streamlineLoadingStage = STREAMLINE_LOADING_STAGES.DATA;
  }

  function setFieldProgress(progress: number) {
    // Field preparation and path integration contribute half each.
    store.streamlineProgress = Math.floor(progress / 2);
  }

  async function prepareField(isCurrent: () => boolean) {
    if (disposed || !isCurrent() || !store.isStreamlineLayerEnabled()) {
      return false;
    }
    store.streamlineLoadingStage = STREAMLINE_LOADING_STAGES.FIELD;
    setFieldProgress(0);
    // Allow the loading indicator to paint before synchronous field setup.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    return !disposed && isCurrent() && store.isStreamlineLayerEnabled();
  }

  // eslint-disable-next-line max-lines-per-function
  async function setField(
    field: TStreamlineVectorField,
    pair: TVectorVariablePair,
    isCurrent: () => boolean,
    beforeInstall?: () => Promise<void>
  ) {
    if (disposed || !isCurrent()) {
      return false;
    }
    // Keep the previous timestep visible until its replacement is ready.
    const revision = ++buildRevision;
    store.setStreamlinePair(pair);
    store.streamlineLoading = true;
    store.streamlineLoadingStage = STREAMLINE_LOADING_STAGES.PATHS;
    store.streamlineProgress = 50;
    try {
      const isCancelled = () =>
        disposed ||
        revision !== buildRevision ||
        !isCurrent() ||
        !store.isStreamlineLayerEnabled();
      const nextLayer = await StreamlineParticleLayer.create(
        field,
        options.projectionHelper.value,
        isCancelled,
        (progress) => {
          if (!isCancelled()) {
            // Reserve completion until the replacement background is ready.
            store.streamlineProgress = Math.min(
              99,
              50 + Math.floor(progress / 2)
            );
          }
        }
      );
      if (!nextLayer || isCancelled()) {
        nextLayer?.dispose();
        return false;
      }
      try {
        await beforeInstall?.();
      } catch (error) {
        nextLayer.dispose();
        throw error;
      }
      if (isCancelled()) {
        nextLayer.dispose();
        return false;
      }
      installLayer(nextLayer);
      store.streamlineProgress = 100;
      return true;
    } finally {
      if (revision === buildRevision && isCurrent()) {
        store.streamlineLoading = false;
        store.streamlineProgress = undefined;
      }
    }
  }

  function showCached() {
    if (!layer) {
      return false;
    }
    updateAppearance();
    return true;
  }

  options.onProjectionChange(() => {
    layer?.updateProjection(options.projectionHelper.value);
    options.redraw();
  });
  watch(() => store.layerStack, updateAppearance, { deep: true });
  onScopeDispose(() => {
    disposed = true;
    // Scalar-variable changes remount the grid renderer. Dispose its GPU
    // objects without clearing the independently selected streamline pair;
    // the replacement renderer will rebuild the layer from that pair.
    disposeObject();
  });

  return {
    clear,
    setAvailablePair,
    startLoading,
    setFieldProgress,
    prepareField,
    setField,
    showCached,
  };
}
